import type {
  CreditAllocation,
  CreditLedgerEntry,
} from "@global/types/credit";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { apiClient } from "../../api";

function formatDate(value: string | null | undefined) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString();
}

function isExpired(allocation: CreditAllocation) {
  return new Date(allocation.expiryDate).getTime() <= Date.now();
}

interface CompanyCreditsPanelProps {
  companyId: string;
}

export const CompanyCreditsPanel: React.FC<CompanyCreditsPanelProps> = ({
  companyId,
}) => {
  const [allocations, setAllocations] = useState<CreditAllocation[]>([]);
  const [ledger, setLedger] = useState<CreditLedgerEntry[]>([]);
  const [availableCredits, setAvailableCredits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCredits, setNewCredits] = useState("100");
  const [newExpiry, setNewExpiry] = useState("");
  const [newNote, setNewNote] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiClient.getCreditAllocations(companyId);
      setAllocations(result.allocations);
      setLedger(result.ledger);
      setAvailableCredits(result.availableCredits);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load credits");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddAllocation = async () => {
    const credits = Number.parseInt(newCredits, 10);
    if (!Number.isFinite(credits) || credits < 1) {
      setError("Enter a positive number of credits.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await apiClient.createCreditAllocation(companyId, {
        credits,
        expiryDate: newExpiry
          ? new Date(newExpiry).toISOString()
          : undefined,
        note: newNote.trim() || undefined,
      });
      setAddDialogOpen(false);
      setNewCredits("100");
      setNewExpiry("");
      setNewNote("");
      await load();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add allocation",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="subtitle1">
          Available credits: <strong>{availableCredits}</strong>
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
            disabled={saving}
          >
            Add Allocation
          </Button>
        </Box>
      </Box>

      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        Credits are consumed when a business case is created and once a month per
        active project. Allocations expire two years after they are granted
        unless an earlier date is set.
      </Alert>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      ) : allocations.length > 0 ? (
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Granted</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell align="right">Original</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allocations.map((allocation) => {
                const expired = isExpired(allocation);
                return (
                  <TableRow key={allocation.allocationId}>
                    <TableCell>{formatDate(allocation.createdDate)}</TableCell>
                    <TableCell align="right">
                      {allocation.remainingCredits}
                    </TableCell>
                    <TableCell align="right">
                      {allocation.originalCredits}
                    </TableCell>
                    <TableCell>{formatDate(allocation.expiryDate)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={expired ? "default" : "success"}
                        label={expired ? "Expired" : "Active"}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {allocation.note ?? ""}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          sx={{ py: 2 }}
        >
          No credit allocations yet
        </Typography>
      )}

      {ledger.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Recent consumption
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Credits</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Project</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledger.map((entry) => (
                  <TableRow key={entry.entryId}>
                    <TableCell>{formatDate(entry.date)}</TableCell>
                    <TableCell align="right">{entry.amount}</TableCell>
                    <TableCell>{entry.reason}</TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {entry.projectId
                          ? `${entry.projectId.slice(0, 8)}...`
                          : "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Credit Allocation</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Credits"
            type="number"
            value={newCredits}
            onChange={(event) => setNewCredits(event.target.value)}
            inputProps={{ min: 1 }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Expiry date (optional, defaults to +2 years)"
            type="date"
            value={newExpiry}
            onChange={(event) => setNewExpiry(event.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            margin="dense"
            label="Note (optional)"
            value={newNote}
            onChange={(event) => setNewNote(event.target.value)}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddAllocation}
            disabled={saving}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
